import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Abas, Aviso, Botao, Chip, PainelDaAba, Segmentado } from "../componentes/ui";
import { ConfiguracaoDoChat } from "../componentes/webchat/ConfiguracaoDoChat";
import { PainelDaConversa } from "../componentes/webchat/PainelDaConversa";
import { mensagemDoConsole } from "../nucleo/console";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useEquipesDeAtendimento } from "../nucleo/tickets";
import { useTitulo } from "../nucleo/titulo";
import {
  CHAVE_DO_WEBCHAT,
  FRASE_DO_AVISO,
  esperaDaConversa,
  useFilaDoChat,
  useFilaDoChatAoVivo,
  type ConversaDoAtendimento,
} from "../nucleo/webchat";

type Aba = "atendimento" | "configuracao";
type Recorte = "abertas" | "encerradas";

/**
 * O WebChat da equipe: a fila de conversas em tempo real, a conversa aberta e, para a
 * administração, a configuração do chat de cada empresa.
 */
export function WebChat() {
  useTitulo("WebChat");
  const sessao = useSessao();
  const [aba, setAba] = useState<Aba>("atendimento");

  if (!sessao.data) {
    return (
      <p className="lead" role="status">
        Verificando o seu acesso…
      </p>
    );
  }
  const podeAtender = podeNaSessao(sessao.data, "webchat:atender");
  const podeGerenciar = podeNaSessao(sessao.data, "webchat:gerenciar");
  if (!podeAtender) {
    return (
      <Aviso tom="marca" data-testid="webchat-sem-permissao">
        O atendimento por chat é feito pela equipe da GEOvendas.
      </Aviso>
    );
  }

  return (
    <div data-testid="webchat">
      <h1 className="titulo-pagina">WebChat</h1>
      <p className="lead mb-4">
        Conversas em tempo real com os clientes: aceite da fila, responda, transfira e encerre — a
        conversa vira registro no chamado.
      </p>
      {podeGerenciar ? (
        <>
          <Abas
            rotulo="Seções do WebChat"
            id="webchat"
            ativa={aba}
            aoSelecionar={setAba}
            itens={[
              { chave: "atendimento", rotulo: "Atendimento" },
              { chave: "configuracao", rotulo: "Configuração" },
            ]}
          />
          <PainelDaAba id="webchat" chave="atendimento" ativa={aba}>
            <Atendimento eu={sessao.data.usuario.id} />
          </PainelDaAba>
          <PainelDaAba id="webchat" chave="configuracao" ativa={aba}>
            <ConfiguracaoDoChat />
          </PainelDaAba>
        </>
      ) : (
        <Atendimento eu={sessao.data.usuario.id} />
      )}
    </div>
  );
}

function Atendimento({ eu }: { eu: string }) {
  const cliente = useQueryClient();
  const equipes = useEquipesDeAtendimento();
  const [equipeId, setEquipeId] = useState("");
  const [recorte, setRecorte] = useState<Recorte>("abertas");
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const fila = useFilaDoChat({ equipeId, encerradas: recorte === "encerradas" });

  useFilaDoChatAoVivo(true, (novo) => {
    setAviso(FRASE_DO_AVISO[novo.tipo]);
    void cliente.invalidateQueries({ queryKey: CHAVE_DO_WEBCHAT });
  });

  const conversa = fila.data?.find((item) => item.id === escolhida) ?? null;

  function concluir(frase: string) {
    setEscolhida(null);
    setAviso(frase);
  }

  return (
    <div className="chat-atendimento">
      <section aria-label="Fila de conversas" className="chat-atendimento__fila">
        <div className="campo">
          <label className="campo__rotulo" htmlFor="chat-filtro-equipe">
            Equipe
          </label>
          <select
            id="chat-filtro-equipe"
            className="campo__controle"
            value={equipeId}
            onChange={(evento) => setEquipeId(evento.target.value)}
          >
            <option value="">Todas as minhas equipes</option>
            {(equipes.data ?? []).map((equipe) => (
              <option key={equipe.id} value={equipe.id}>
                {equipe.nome}
              </option>
            ))}
          </select>
        </div>
        <Segmentado
          rotulo="Quais conversas mostrar"
          valor={recorte}
          aoMudar={(valor) => {
            setRecorte(valor);
            setEscolhida(null);
          }}
          itens={[
            { valor: "abertas", rotulo: "Em aberto" },
            { valor: "encerradas", rotulo: "Encerradas (7 dias)" },
          ]}
        />
        {aviso ? (
          <p className="nota-rodape mt-2" role="status" data-testid="chat-aviso-da-fila">
            {aviso}
          </p>
        ) : null}
        {fila.isPending ? <p role="status">Carregando a fila…</p> : null}
        {fila.isError ? <Aviso tom="erro">{mensagemDoConsole(fila.error)}</Aviso> : null}
        {fila.data && fila.data.length === 0 ? (
          <Aviso tom="marca" data-testid="chat-fila-vazia">
            {recorte === "abertas" ? "Nenhuma conversa na fila agora." : "Nenhuma conversa encerrada na última semana."}
          </Aviso>
        ) : null}
        <ul className="chat-fila" data-testid="chat-fila">
          {fila.data?.map((item) => (
            <ItemDaFila
              key={item.id}
              item={item}
              eu={eu}
              escolhida={item.id === escolhida}
              aoEscolher={() => setEscolhida(item.id)}
            />
          ))}
        </ul>
      </section>
      <section aria-label="Conversa" className="chat-atendimento__conversa">
        {conversa ? (
          <PainelDaConversa key={conversa.id} conversa={conversa} aoConcluir={concluir} />
        ) : (
          <Aviso tom="marca" data-testid="chat-nenhuma-escolhida">
            Escolha uma conversa da fila para atender.
          </Aviso>
        )}
        {escolhida && !conversa && fila.data ? (
          <Botao variante="fantasma" tamanho="pequeno" className="mt-2" onClick={() => setEscolhida(null)}>
            Voltar para a fila
          </Botao>
        ) : null}
      </section>
    </div>
  );
}

function ItemDaFila({
  item,
  eu,
  escolhida,
  aoEscolher,
}: {
  item: ConversaDoAtendimento;
  eu: string;
  escolhida: boolean;
  aoEscolher: () => void;
}) {
  const minha = item.atendente_id === eu;
  return (
    <li>
      <button
        type="button"
        className={escolhida ? "chat-fila__item chat-fila__item--escolhida" : "chat-fila__item"}
        aria-pressed={escolhida}
        data-testid={`chat-fila-item-${item.organizacao_nome}`}
        data-estado={item.estado}
        onClick={aoEscolher}
      >
        <span className="chat-fila__linha">
          <b>{item.organizacao_nome}</b>
          <Chip estado={item.estado === "fila" ? "aguardando" : item.estado === "ativa" ? "ok" : "neutro"}>
            {item.estado === "fila" ? "Na fila" : item.estado === "ativa" ? (minha ? "Com você" : "Em atendimento") : "Encerrada"}
          </Chip>
        </span>
        <span className="chat-fila__previa">{item.previa ?? "Sem mensagem"}</span>
        <span className="nota-rodape">
          {item.solicitante_nome ?? "Pessoa removida"} · há {esperaDaConversa(item.criada_em)}
          {item.fora_do_horario ? " · fora do horário" : ""}
        </span>
      </button>
    </li>
  );
}
